import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { PaginationFooter } from '@/components/shared/PaginationFooter';
import { Plus, Search, Check, X } from 'lucide-react';
import { toast } from 'sonner';

export default function LeaveApprovalsPage() {
  const [leaves, setLeaves] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('PENDING');
  const { hasPermission } = useAuth();

  const fetchData = async () => {
    try {
      const params = new URLSearchParams({ page, pageSize: 20 });
      if (statusFilter !== 'all') params.set('status', statusFilter);
      const res = await api.get(`/leave?${params}`);
      setLeaves(res.data.items || res.data.data || []);
      setTotal(res.data.total || 0);
    } catch (e) { toast.error('Failed to load leave requests'); }
  };
  useEffect(() => { fetchData(); }, [page, statusFilter]); // eslint-disable-line

  const handleAction = async (id, action, notes = '') => {
    try {
      await api.post(`/leave/${id}/${action}`, { notes });
      toast.success(`Leave ${action}d`); fetchData();
    } catch (e) { toast.error(e.response?.data?.error?.message || 'Action failed'); }
  };

  const exportCSV = async () => {
    try {
      const res = await api.get('/leave/export-csv', { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data])); const a = document.createElement('a');
      a.href = url; a.download = 'leave_requests.csv'; a.click();
    } catch (e) { toast.error('Export failed'); }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <div><h1 className="text-2xl font-bold tracking-tight">Leave Management</h1><p className="text-muted-foreground mt-1">Approve or reject leave requests</p></div>
        <Button variant="outline" onClick={exportCSV}>Export CSV</Button>
      </div>
      <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
        <SelectTrigger className="w-[160px]"><SelectValue /></SelectTrigger>
        <SelectContent><SelectItem value="all">All</SelectItem><SelectItem value="PENDING">Pending</SelectItem><SelectItem value="APPROVED">Approved</SelectItem><SelectItem value="REJECTED">Rejected</SelectItem><SelectItem value="CANCELLED">Cancelled</SelectItem></SelectContent>
      </Select>
      <Card><CardContent className="p-0">
        <Table>
          <TableHeader><TableRow><TableHead>Staff</TableHead><TableHead>Type</TableHead><TableHead>From</TableHead><TableHead>To</TableHead><TableHead>Reason</TableHead><TableHead>Status</TableHead><TableHead>Actions</TableHead></TableRow></TableHeader>
          <TableBody>
            {leaves.length === 0 ? <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">No leave requests</TableCell></TableRow>
            : leaves.map(l => (
              <TableRow key={l.id}>
                <TableCell className="font-medium">{l.staff_name || l.staff_id?.slice(0,8)}</TableCell>
                <TableCell><StatusBadge status={l.type} /></TableCell>
                <TableCell className="font-mono text-xs">{l.start_at?.slice(0,10)}</TableCell>
                <TableCell className="font-mono text-xs">{l.end_at?.slice(0,10)}</TableCell>
                <TableCell className="text-sm text-muted-foreground">{l.reason || '-'}</TableCell>
                <TableCell><StatusBadge status={l.status} /></TableCell>
                <TableCell>
                  {l.status === 'PENDING' && hasPermission('leave.approve') && (
                    <div className="flex gap-1">
                      <Button size="sm" onClick={() => handleAction(l.id, 'approve')}><Check className="h-3 w-3 mr-1" /> Approve</Button>
                      <Button size="sm" variant="ghost" className="text-destructive" onClick={() => handleAction(l.id, 'reject')}><X className="h-3 w-3 mr-1" /> Reject</Button>
                    </div>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <PaginationFooter page={page} pageSize={20} total={total} onPageChange={setPage} />
      </CardContent></Card>
    </div>
  );
}
